import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getOperatorType,
  getActiveOperator,
  saveConsumer,
} from "../../../services/panel/panelServices";
import toast from "../../../utils/toast";

const EMPTY_FORM = {
  categoryId: "",
  oid: "",
  accountNumber: "",
  consumerName: "",
  nickname: "",
};

export default function AddCASingle() {
  const navigate = useNavigate();
  // "Add account" on Biller list sends the operator here
  const presetOperator = useLocation().state?.operator;

  const [isCategoryData, setCategoryData] = useState([]);
  const [isBillerData, setBillerData] = useState([]);
  const [billerLoading, setBillerLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCategoryList();
  }, []);

  const fetchCategoryList = async () => {
    try {
      const res = await getOperatorType();
      if (res?.statuscode === 1) {
        setCategoryData(res?.data?.assignedOpTypes || []);
        if (presetOperator) {
          setForm((f) => ({ ...f, categoryId: String(presetOperator.opType) }));
          fetchBillerList(presetOperator.opType, presetOperator.oid);
        }
      }
    } catch (err) {
      console.log(
        err.response?.data?.msg || "Failed to fetch categories",
        "error",
      );
    }
  };

  const fetchBillerList = async (categoryId, selectOid = "") => {
    setBillerLoading(true);
    setBillerData([]);
    try {
      const res = await getActiveOperator({ OType: Number(categoryId) });
      // This API returns the list in `operators`, not in `data`
      if (res?.statuscode === 1) {
        setBillerData(res?.operators || []);
        setForm((f) => ({ ...f, oid: selectOid ? String(selectOid) : "" }));
      }
    } catch (err) {
      console.log(
        err.response?.data?.msg || "Failed to fetch billers",
        "error",
      );
    } finally {
      setBillerLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((er) => ({ ...er, [name]: "" }));
  };

  // New category → clear the selected biller and load that category's billers
  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setForm((f) => ({ ...f, categoryId, oid: "" }));
    setErrors((er) => ({ ...er, categoryId: "", oid: "" }));
    if (categoryId) fetchBillerList(categoryId);
    else setBillerData([]);
  };

  const validate = () => {
    const e = {};
    if (!form.categoryId) e.categoryId = "Select a category.";
    if (!form.oid) e.oid = "Select a biller.";
    if (!form.accountNumber.trim())
      e.accountNumber = "Account number is required.";
    if (!form.consumerName.trim())
      e.consumerName = "Consumer name is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        CategoryID: Number(form.categoryId),
        OID: Number(form.oid),
        AccountNumber: form.accountNumber.trim(),
        Nickname: form.nickname.trim(),
        ConsumerName: form.consumerName.trim(),
      };
      const res = await saveConsumer(payload);
      if (res?.statuscode === 1) {
        toast.success(res?.msg || "Account saved.");
        navigate("/saved-ca");
      } else {
        toast.error(res?.msg || "Failed to save account.");
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Failed to save account.");
    } finally {
      setSaving(false);
    }
  };

  const selectedBiller = isBillerData.find(
    (b) => String(b.oid) === String(form.oid),
  );

  const errorText = (key) =>
    errors[key] ? (
      <span style={{ color: "#dc2626", fontSize: 11 }}>{errors[key]}</span>
    ) : null;
  const errorBorder = (key) => (errors[key] ? { borderColor: "#dc2626" } : {});

  return (
    <>
      <div className="screen active" id="screen-ca-single">
        <div className="page-head">
          <div>
            <h1>Add consumer account — single</h1>
            <div className="ph-sub">
              Register one consumer account against a biller
            </div>
          </div>
        </div>
        <div className="grid-2">
          <div className="card">
            <div className="form-grid">
              <div className="field">
                <label>Category</label>
                <select
                  name="categoryId"
                  value={form.categoryId}
                  onChange={handleCategoryChange}
                  style={errorBorder("categoryId")}
                >
                  <option value="">Select category</option>
                  {isCategoryData.map((c) => (
                    <option key={c.serviceID} value={c.serviceID}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {errorText("categoryId")}
              </div>
              <div className="field">
                <label>Biller</label>
                <select
                  name="oid"
                  value={form.oid}
                  onChange={handleChange}
                  disabled={!form.categoryId || billerLoading}
                  style={errorBorder("oid")}
                >
                  <option value="">
                    {billerLoading
                      ? "Loading billers..."
                      : !form.categoryId
                        ? "Select category first"
                        : isBillerData.length
                          ? "Select biller"
                          : "No billers found"}
                  </option>
                  {isBillerData.map((b) => (
                    <option key={b.oid} value={b.oid}>
                      {b.name}
                    </option>
                  ))}
                </select>
                {errorText("oid")}
                {selectedBiller && (
                  <div className="hint">
                    Biller ID: {selectedBiller.billerID || selectedBiller.opid}
                  </div>
                )}
              </div>
            </div>
            <div className="form-grid">
              <div className="field">
                <label>Consumer / account number</label>
                <input
                  type="text"
                  name="accountNumber"
                  value={form.accountNumber}
                  onChange={handleChange}
                  placeholder="e.g. 400213558812"
                  style={errorBorder("accountNumber")}
                />
                {errorText("accountNumber")}
              </div>
              <div className="field">
                <label>Consumer name</label>
                <input
                  type="text"
                  name="consumerName"
                  value={form.consumerName}
                  onChange={handleChange}
                  placeholder="e.g. M/S Verma Textiles"
                  style={errorBorder("consumerName")}
                />
                {errorText("consumerName")}
              </div>
            </div>
            <div className="field">
              <label>Nickname</label>
              <input
                type="text"
                name="nickname"
                value={form.nickname}
                onChange={handleChange}
                placeholder="e.g. Warehouse 2 electricity"
              />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-ghost">Fetch &amp; validate</button>

              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save account"}
              </button>
            </div>
          </div>
          <div className="card">
            <div className="card-title">Why validate first?</div>
            <div className="card-sub">
              Fetching confirms the account is live with the biller before you
              save it
            </div>
            <div className="checklist">
              <div className="check-row">
                <div className="cr-num">✓</div>
                <div style={{ fontSize: "12.3px" }}>
                  Confirms consumer name matches biller records
                </div>
              </div>
              <div className="check-row">
                <div className="cr-num">✓</div>
                <div style={{ fontSize: "12.3px" }}>
                  Pulls current outstanding amount &amp; due date
                </div>
              </div>
              <div className="check-row">
                <div className="cr-num">✓</div>
                <div style={{ fontSize: "12.3px" }}>
                  Flags inactive or invalid account numbers early
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
